import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../../../shared/shared.api';
import { getTripTimeline } from '../../dispatch/features/trip-timeline/trip-timeline.api';
import { tripStops, type TripStop } from '../policies/trip-stops.policy';
import { getTripEta, getTripPage, type TripEta, type TripPageData } from '../trips.api';

export type TripPageState =
  | { status: 'loading' }
  | { status: 'loaded'; trip: TripPageData; eta: TripEta | null; stops: TripStop[] }
  | { status: 'forbidden' }
  | { status: 'not-found' }
  | { status: 'error' };

const ETA_REFRESH_MS = 60_000;

function toErrorState(err: unknown): TripPageState {
  if (err instanceof ApiError && err.status === 403) return { status: 'forbidden' };
  if (err instanceof ApiError && err.status === 404) return { status: 'not-found' };
  return { status: 'error' };
}

/** The ETA only decorates the header, so a failed read leaves the deadlines out instead of failing the page. */
async function readEta(tripId: string): Promise<TripEta | null> {
  try {
    return await getTripEta(tripId);
  } catch {
    return null;
  }
}

/** The plan's stops only decorate the header and the map, so a failed read leaves them out instead of failing the page. */
async function readStops(tripId: string, eta: TripEta | null): Promise<TripStop[]> {
  try {
    return tripStops((await getTripTimeline(tripId)).stops, eta);
  } catch {
    return [];
  }
}

async function readTripWithEstimate(tripId: string) {
  const [trip, eta] = await Promise.all([getTripPage(tripId), readEta(tripId)]);
  return { trip, eta, stops: await readStops(tripId, eta) };
}

/**
 * The trip, its plan's stops and its deadline verdicts. There is no stream of trip events, so the verdicts are re-read every minute and `reload` is
 * what an action or a fresh fix calls to pull the trip and the verdicts again without going back to the skeleton.
 */
export function useTripPage(tripId: string) {
  const [state, setState] = useState<TripPageState>({ status: 'loading' });
  const [readCount, setReadCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    readTripWithEstimate(tripId)
      .then((loaded) => {
        if (!cancelled) setState({ status: 'loaded', ...loaded });
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tripId, readCount]);

  const reload = useCallback(() => setReadCount((count) => count + 1), []);

  useEffect(() => {
    const timer = setInterval(reload, ETA_REFRESH_MS);
    return () => clearInterval(timer);
  }, [reload]);

  return { state, reload, readCount };
}
